import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ICON } from '../icons/iconIds';
import { selkirkIconImgHtml } from '../icons/getSelkirkIconUrl';
import SelkirkIcon from './icons/SelkirkIcon';
import IconText from './icons/IconText';
import './SampleLocationMap.css';

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Geographic locations - matching actual database values
const LOCATIONS = {
  'Selkirk Copper Mine': { 
    coords: [62.5, -137.4], 
    color: '#06b6d4', // Cyan/Blue
    iconId: ICON.sampleLocDrill,
    label: 'Selkirk Copper Mine',
    order: 1
  },
  'En Route to Whitehorse': { 
    coords: [61.6, -136.2], // Midpoint between Mine and Whitehorse
    color: '#f59e0b', // Amber
    iconId: ICON.sampleLocTruck,
    label: 'En Route to Whitehorse',
    order: 2,
    isTransit: true,
    fromCoords: [62.5, -137.4], // From Minto
    toCoords: [60.7, -135.0]    // To Whitehorse
  },
  'Whitehorse': { 
    coords: [60.7, -135.0], 
    color: '#f97316', // Orange
    iconId: ICON.sampleLocLab,
    label: 'Whitehorse Lab',
    order: 3
  },
  'En Route to Vancouver': { 
    coords: [55.0, -129.0], // Midpoint between Whitehorse and Vancouver
    color: '#f59e0b', // Amber
    iconId: ICON.sampleLocAir,
    label: 'En Route to Vancouver',
    order: 4,
    isTransit: true,
    fromCoords: [60.7, -135.0],  // From Whitehorse
    toCoords: [49.2, -123.1]     // To Vancouver
  },
  'Vancouver': { 
    coords: [49.2, -123.1], 
    color: '#8b5cf6', // Purple
    iconId: ICON.sampleLocOffice,
    label: 'Vancouver (ALS Lab)',
    order: 5
  },
  'Database': { 
    coords: [52.0, -135.0], // Pacific Ocean - further north and west
    color: '#10b981', // Green
    iconId: ICON.sampleLocComplete,
    label: 'Database (Finalized)',
    order: 6,
    isVirtual: true
  }
};

const SampleLocationMap = ({ data, onLocationClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Aggregate data by location
  const locationData = React.useMemo(() => {
    if (!data || data.length === 0) return {};
    
    const aggregated = {};
    
    // Initialize all locations
    Object.keys(LOCATIONS).forEach(loc => {
      aggregated[loc] = {
        count: 0,
        dispatches: [],
        ...LOCATIONS[loc]
      };
    });
    
    // Group by location
    data.forEach(item => {
      const location = item.location || 'Unknown';
      
      // Use dispatch_sample_count as it's the total for the dispatch
      const sampleCount = parseInt(item.dispatch_sample_count || item.sample_count || 0);
      
      if (aggregated[location]) {
        aggregated[location].count += isNaN(sampleCount) ? 0 : sampleCount;
        aggregated[location].dispatches.push(item);
      } else {
        // Log unknown locations for debugging
        console.warn(`Unknown location: "${location}" - dispatch: ${item.dispatch}`);
      }
    });
    
    // Log aggregated data for debugging
    console.log('📍 Sample location aggregated data:', aggregated);
    
    return aggregated;
  }, [data]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const map = L.map(mapRef.current, {
        center: [56.0, -130.0], // Center between locations
        zoom: 5,
        zoomControl: true,
        minZoom: 4,
        maxZoom: 10
      });

      // Add dark satellite base layer
      const satelliteMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© Esri',
        maxZoom: 18
      });

      satelliteMap.addTo(map);

      // Add layer control
      const baseLayers = {
        "Satellite": satelliteMap,
        "Terrain": L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenTopoMap'
        }),
        "Dark": L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          attribution: '© CartoDB'
        })
      };

      L.control.layers(baseLayers, {}, { position: 'topleft' }).addTo(map);
      L.control.scale({ position: 'bottomleft' }).addTo(map);

      mapInstanceRef.current = map;
      setMapReady(true);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Add markers and routes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !locationData) return;

    const map = mapInstanceRef.current;

    // Clear existing layers (except base layer)
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        map.removeLayer(layer);
      }
    });

    // Get all locations in order - show all locations regardless of count
    const orderedLocations = Object.entries(locationData)
      .sort(([, a], [, b]) => a.order - b.order);

    // Draw routes between locations with fatter lines for transit
    const routeSegments = [
      { from: 'Selkirk Copper Mine', to: 'Whitehorse', color: '#6b7280' },
      { from: 'Whitehorse', to: 'Vancouver', color: '#6b7280' },
      { from: 'Vancouver', to: 'Database', color: '#6b7280' }
    ];

    routeSegments.forEach(segment => {
      const fromLoc = LOCATIONS[segment.from];
      const toLoc = LOCATIONS[segment.to];
      
      if (fromLoc && toLoc) {
        // Draw route line
        L.polyline(
          [fromLoc.coords, toLoc.coords],
          {
            color: segment.color,
            weight: 4, // Fatter lines
            opacity: 0.5,
            dashArray: '10, 8'
          }
        ).addTo(map);
      }
    });

    // Add markers for each location
    orderedLocations.forEach(([name, loc]) => {
      const count = loc.count;
      const hasData = count > 0;

      // Create custom icon with count
      const icon = L.divIcon({
        className: 'sample-location-marker',
        html: `
          <div style="position: relative; width: 60px; height: 60px; transform: translate(-50%, -50%);">
            ${hasData && !loc.isVirtual ? `
              <div class="pulse-ring" style="
                position: absolute;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                border: 2px solid ${loc.color};
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                animation: pulse-ring 2s ease-out infinite;
                pointer-events: none;
              "></div>
            ` : ''}
            <div style="
              background-color: ${loc.color};
              width: 40px;
              height: 40px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 4px 12px rgba(0,0,0,0.4);
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 18px;
              cursor: ${hasData ? 'pointer' : 'default'};
              transition: all 0.3s ease;
            " onmouseover="this.style.transform='translate(-50%, -50%) scale(1.1)'" 
               onmouseout="this.style.transform='translate(-50%, -50%) scale(1)'"
            >${selkirkIconImgHtml(loc.iconId, 18)}</div>
            ${hasData ? `
              <div style="
                position: absolute;
                top: -8px;
                right: -8px;
                background: ${loc.color};
                color: white;
                border-radius: 12px;
                padding: 2px 8px;
                font-size: 12px;
                font-weight: 700;
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                min-width: 24px;
                text-align: center;
              ">${count}</div>
            ` : ''}
          </div>
        `,
        iconSize: [60, 60],
        iconAnchor: [30, 30]
      });

      const marker = L.marker(loc.coords, { 
        icon,
        zIndexOffset: loc.order * 100
      }).addTo(map);

      // Create popup content
      const popupContent = `
        <div style="min-width: 200px; color: #1f2937;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">
            ${selkirkIconImgHtml(loc.iconId, 16)} ${loc.label}
          </h3>
          <div style="font-size: 14px; color: #4b5563; margin-bottom: 8px;">
            ${count} sample${count !== 1 ? 's' : ''}
          </div>
          ${hasData ? `
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
              ${loc.dispatches.length} dispatch${loc.dispatches.length !== 1 ? 'es' : ''}
            </div>
            <button class="location-details-btn" data-location="${name}" 
                    style="
                      background: ${loc.color}; 
                      color: white; 
                      border: none; 
                      padding: 6px 12px; 
                      border-radius: 4px; 
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: 600;
                      width: 100%;
                    ">
              View Details →
            </button>
          ` : '<div style="font-size: 12px; color: #9ca3af;">No samples at this location</div>'}
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: 250,
        className: 'sample-location-popup'
      });

      // Click handler - direct marker click
      if (hasData) {
        marker.on('click', () => {
          if (onLocationClick) {
            setSelectedLocation(name);
            onLocationClick(name, loc.dispatches);
          }
        });
      }
    });

    // Fit bounds to show all markers
    const bounds = orderedLocations
      .filter(([, loc]) => !loc.isVirtual)
      .map(([, loc]) => loc.coords);
    
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [80, 80] });
    }

  }, [mapReady, locationData, onLocationClick]);

  // Listen for button clicks in popups using event delegation
  useEffect(() => {
    if (!mapRef.current) return;

    const handlePopupClick = (e) => {
      const btn = e.target.closest('.location-details-btn');
      if (btn) {
        const locationName = btn.getAttribute('data-location');
        const loc = locationData[locationName];
        if (loc && loc.dispatches && loc.dispatches.length > 0 && onLocationClick) {
          setSelectedLocation(locationName);
          onLocationClick(locationName, loc.dispatches);
        }
      }
    };

    const mapContainer = mapRef.current;
    mapContainer.addEventListener('click', handlePopupClick);
    return () => mapContainer.removeEventListener('click', handlePopupClick);
  }, [locationData, onLocationClick]);

  return (
    <div className="sample-location-map-container">
      <div ref={mapRef} className="sample-location-map" style={{ height: '600px', width: '100%', borderRadius: '8px' }} />
      
      {/* Legend */}
      <div className="location-map-legend">
        <IconText
          icon={ICON.uiLocationPin}
          iconSize={14}
          as="h4"
          className="location-map-legend-title"
          gap="0.35rem"
        >
          Sample Locations
        </IconText>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem' }}>
          {Object.entries(LOCATIONS).map(([name, loc]) => {
            const count = locationData[name]?.count || 0;
            const dispatchCount = locationData[name]?.dispatches.length || 0;
            
            return (
              <div 
                key={name}
                onClick={() => {
                  if (count > 0 && onLocationClick) {
                    setSelectedLocation(name);
                    onLocationClick(name, locationData[name].dispatches);
                  }
                }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.4rem',
                  cursor: count > 0 ? 'pointer' : 'default',
                  padding: '0.35rem 0.5rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  opacity: count > 0 ? 1 : 0.5,
                  background: selectedLocation === name ? 'rgba(249, 190, 11, 0.15)' : 'transparent'
                }}
              >
                <div style={{ 
                  fontSize: '16px',
                  flexShrink: 0,
                  lineHeight: 1
                }}><SelkirkIcon name={loc.iconId} size={16} decorative /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    color: '#e5e7eb',
                    fontWeight: '500',
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }}>
                    {loc.label}
                  </div>
                  {count > 0 && (
                    <div style={{ 
                      fontSize: '0.625rem', 
                      color: '#9ca3af',
                      lineHeight: 1.3,
                      marginTop: '2px'
                    }}>
                      {count.toLocaleString()} samples • {dispatchCount} dispatches
                    </div>
                  )}
                </div>
                {count > 0 && (
                  <div style={{
                    background: loc.color,
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '0.625rem',
                    fontWeight: '700',
                    minWidth: '22px',
                    textAlign: 'center',
                    lineHeight: 1.2
                  }}>{count.toLocaleString()}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SampleLocationMap;

